import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import {
  getPurchaseOrders, getPurchaseOrder, createPurchaseOrder, updatePurchaseOrder, deletePurchaseOrder, PO_STATUSES,
} from "../lib/purchaseOrders";
import { getSuppliers } from "../lib/suppliers";
import { getProducts } from "../lib/products";
import PurchaseOrderForm from "./PurchaseOrderForm";
import PurchaseOrderView, { POStatusBadge } from "./PurchaseOrderView";
import { todayISO } from "../lib/dates";

const PAGE_SIZE = 25;
const money = (po) => `${po.preferred_currency === "GBP" ? "£" : po.preferred_currency === "USD" ? "$" : "€"}${(Number(po.total_amount) || 0).toFixed(2)}`;

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function PurchaseOrderManager({ autoNew, onAutoNew }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [supplierId, setSupplierId] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // full PO | {} | null
  const [viewing, setViewing] = useState(null); // full PO | null

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data, count: total } = await getPurchaseOrders({ search, status, supplierId, page, pageSize: PAGE_SIZE, sortBy, sortDir });
      setRows(data); setCount(total);
    } catch (e) { setError(e.message || "Could not load purchase orders."); }
    finally { setLoading(false); }
  }, [search, status, supplierId, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  // Master data for the supplier filter + the form's supplier/product pickers.
  const loadRefs = useCallback(async () => {
    try { const { data } = await getSuppliers({ status: "active", pageSize: 1000, sortBy: "name" }); setSuppliers(data); } catch { /* surfaced via PO load */ }
    try { const { data } = await getProducts({ pageSize: 1000, sortBy: "name" }); setProducts(data); } catch { /* picker degrades to empty */ }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onSupplier = (v) => { setSupplierId(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setSupplierId("all"); setPage(1); };

  const openNew = () => setEditing({});
  const openEdit = async (po) => { try { setEditing(await getPurchaseOrder(po.id)); } catch (e) { setError(e.message); } };
  const openView = async (po) => { try { setViewing(await getPurchaseOrder(po.id)); } catch (e) { setError(e.message); } };

  const save = async (payload) => {
    if (editing && editing.id) await updatePurchaseOrder(editing.id, payload);
    else await createPurchaseOrder(payload);
    setEditing(null);
    await load();
  };
  const del = async (po) => {
    if (po.status !== "draft") return;
    if (!window.confirm(`Delete draft ${po.po_number}?`)) return;
    try { await deletePurchaseOrder(po.id); await load(); } catch (e) { setError(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all" || supplierId !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 150 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search PO number, contact, email…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search purchase orders" />
        <select style={selStyle} value={supplierId} onChange={(e) => onSupplier(e.target.value)} aria-label="Filter by supplier">
          <option value="all">All suppliers</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name}</option>)}
        </select>
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {PO_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} order{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={openNew}>+ New Purchase Order</button>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="PO Number" col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Supplier</th>
              <Th label="Order Date" col="order_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Expected</th>
              <Th label="Total" col="total" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={7} style={S.empty}>
                {hasFilters ? (
                  <div>No purchase orders match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No purchase orders yet</div>
                    <div style={{ marginBottom: 12 }}>Raise your first purchase order to a supplier.</div>
                    <button style={S.btn()} onClick={openNew}>+ New Purchase Order</button>
                  </div>
                )}
              </td></tr>
            ) : rows.map((po) => {
              const overdue = po.expected_delivery_date && po.expected_delivery_date < todayISO() && !["received", "cancelled"].includes(po.status);
              return (
                <tr key={po.id} style={{ cursor: "pointer" }} onClick={() => openView(po)}>
                  <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{po.po_number}</span></td>
                  <td style={S.td}>{po.suppliers?.company_name || <span style={{ color: C.textDim }}>—</span>}</td>
                  <td style={S.td}>{po.order_date || "—"}</td>
                  <td style={S.td}>{po.expected_delivery_date ? <span style={{ color: overdue ? C.warning : C.text, fontWeight: overdue ? 700 : 400 }}>{po.expected_delivery_date}{overdue ? " ⚠" : ""}</span> : "—"}</td>
                  <td style={S.td}><strong>{money(po)}</strong></td>
                  <td style={S.td}><POStatusBadge status={po.status} /></td>
                  <td style={S.td} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button style={S.btn("ghost")} title="View" aria-label={`View ${po.po_number}`} onClick={() => openView(po)}>👁</button>
                      <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${po.po_number}`} onClick={() => openEdit(po)}>✎</button>
                      {po.status === "draft" && <button style={S.btn("ghost")} title="Delete draft" aria-label={`Delete ${po.po_number}`} onClick={() => del(po)}>🗑</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <PurchaseOrderForm initial={editing} suppliers={suppliers} products={products} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {viewing && (
        <PurchaseOrderView po={viewing} onClose={() => setViewing(null)} onEdit={(po) => { setViewing(null); openEdit(po); }} />
      )}
    </div>
  );
}
